import React, { useRef, useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import { FaChevronLeft, FaChevronRight, FaBookOpen } from "react-icons/fa";
import { getRecentBooks } from "../../services/api";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../../contexts/LanguageContext";

const translations = {
  kz: {
    newBooksTitle: "Жаңа кітаптар",
    newBooksSubtitle: "Біздің жаңа кітаптар жинағымызбен танысыңыз",
    newBadge: "Жаңа",
    viewDetails: "Толығырақ көру",
    detailsButton: "Толығырақ",
  },
  ru: {
    newBooksTitle: "Новые книги",
    newBooksSubtitle: "Познакомьтесь с нашей коллекцией новых книг",
    newBadge: "Новая",
    viewDetails: "Подробнее",
    detailsButton: "Подробнее",
  },
  en: {
    newBooksTitle: "New books",
    newBooksSubtitle: "Explore our collection of new books",
    newBadge: "New",
    viewDetails: "View Details",
    detailsButton: "Details",
  },
};

// Функция для умной обрезки текста
const truncateText = (text, maxLength) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  
  // Обрезаем по словам, а не по символам
  const words = text.split(' ');
  let result = '';
  
  for (let i = 0; i < words.length; i++) {
    const newResult = result + (i > 0 ? ' ' : '') + words[i];
    if (newResult.length > maxLength) {
      break;
    }
    result = newResult;
  }
  
  return result + (result.length < text.length ? '...' : '');
};

function NewBooks() {
  const prevRef = useRef(null);
  const nextRef = useRef(null);
  const [books, setBooks] = useState([]);
  const navigate = useNavigate();
  const { currentLanguage } = useLanguage();
  const t = translations[currentLanguage] || translations.kz;

  useEffect(() => {
    const loadBooks = async () => {
      try {
        const res = await getRecentBooks();
        if (res.books) {
          setBooks(res.books);
        }
      } catch (error) {
        console.error("Error loading books:", error);
      }
    };

    loadBooks();
  }, []);

  const handleClickBook = (id) => {
    navigate(`/book/${id}`);
  };

  // Handle swiper navigation
  useEffect(() => {
    const initNavigation = () => {
      try {
        const swiperEl = document.querySelector(".newbooks-section .swiper");

        if (swiperEl && swiperEl.swiper && prevRef.current && nextRef.current) {
          prevRef.current.addEventListener("click", () => {
            swiperEl.swiper.slidePrev();
          });

          nextRef.current.addEventListener("click", () => {
            swiperEl.swiper.slideNext();
          });
        }
      } catch (error) {
        console.error("Error initializing swiper navigation:", error);
      }
    };

    const timer = setTimeout(() => {
      initNavigation();
    }, 500);

    return () => clearTimeout(timer);
  }, [books]);

  return (
    <div className="newbooks-section">
      <div className="newbooks-container">
        <div className="newbooks-header">
          <div className="newbooks-title-line">
            <span className="line" />
            <h2 className="newbooks-title">{t.newBooksTitle}</h2>
            <span className="line" />
          </div>
          <p className="newbooks-subtitle">{t.newBooksSubtitle}</p>
        </div>

        <div className="newbooks-carousel">
          <button
            ref={prevRef}
            className="nav-button prev"
            aria-label="Previous"
          >
            <FaChevronLeft />
          </button>

          <Swiper
            spaceBetween={30}
            slidesPerView={1}
            centeredSlides={true}
            loop={true}
            autoplay={{
              delay: 5000,
              disableOnInteraction: false,
            }}
            pagination={{
              clickable: true,
              dynamicBullets: true,
            }}
            breakpoints={{
              // Мобильные: 1 книга
              320: {
                slidesPerView: 1,
                spaceBetween: 20,
                centeredSlides: true,
              },
              // Планшеты: 1 книга по центру
              481: {
                slidesPerView: 1,
                spaceBetween: 20,
                centeredSlides: true,
              },
              // Средние планшеты: 1 книга
              640: {
                slidesPerView: 1,
                spaceBetween: 25,
                centeredSlides: true,
              },
              // Большие планшеты и ноутбуки: 3 книги с блюром
              769: {
                slidesPerView: 3,
                spaceBetween: 30,
                centeredSlides: true,
              },
              // Десктопы: 3 книги с блюром
              1024: {
                slidesPerView: 3,
                spaceBetween: 30,
                centeredSlides: true,
              },
            }}
            className="newbooks-swiper"
          >
            {books.map((book) => (
              <SwiperSlide key={book.id} className="newbooks-slide">
                <div
                  className="book-card"
                  onClick={() => handleClickBook(book.id)}
                >
                  <div className="book-image-container">
                    <div className="badge-new">{t.newBadge}</div>

                    <div className="book-overlay">
                      <div className="view-details">
                        <FaBookOpen />
                        <span>{t.viewDetails}</span>
                      </div>
                    </div>

                    <div className="book-image-wrapper">
                      <img
                        src={`${process.env.REACT_APP_API_URL}${book.img_url}`}
                        alt={book.title}
                        className="book-image"
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/200x300/e5e7eb/9ca3af?text=Нет+изображения';
                        }}
                      />
                    </div>
                  </div>
                  <div className="book-info">
                    <h3 className="book-title" title={book.title}>
                      {truncateText(book.title, 50)}
                    </h3>
                    <p className="book-author" title={book.author}>
                      {truncateText(book.author, 25)}
                    </p>
                    <div className="book-action">
                      <button className="btn-details">{t.detailsButton}</button>
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>

          <button ref={nextRef} className="nav-button next" aria-label="Next">
            <FaChevronRight />
          </button>
        </div>
      </div>
    </div>
  );
}

export default NewBooks;